import React, { useState, useEffect, useRef, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import {
  MenuItem, Select, TextField, Radio, RadioGroup, FormControlLabel,
  Button, ButtonGroup as MUIButtonGroup,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { timezones } from '../../utils/timezones';
import TextFieldMUI from '@mui/material/TextField';
import Autocomplete from '@mui/lab/Autocomplete';
import { getScheduleStartStore } from './useScheduleStartStore';
import checkKey from '../../utils/CheckKey';
import ErrorIcon from '@mui/icons-material/Error';
import './ScheduleStartStyle.css';
import ValidationErrors from '../pages/ComponentPageDialogs/ValidationErrors';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import CustomPaper from '../common/CustomPaper';
import { send_request } from '../../utils/Request';

const pad2 = (n) => String(n).padStart(2, '0');

const extractDate = (dayjsVal) => {
  if (!dayjsVal) return null;
  const d = dayjsVal.toDate ? dayjsVal.toDate() : new Date(dayjsVal);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const extractTime = (dayjsVal) => {
  if (!dayjsVal) return null;
  const d = dayjsVal.toDate ? dayjsVal.toDate() : new Date(dayjsVal);
  if (isNaN(d.getTime())) return null;
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const TimezoneAutocomplete = ({ value, onChange, width }) => (
  <Autocomplete
    options={timezones}
    value={timezones.find((tz) => tz.name === value) || null}
    size="small"
    onChange={(_, option) => option && onChange(option.name)}
    renderInput={(params) => <TextFieldMUI {...params} variant="outlined" />}
    getOptionLabel={(opt) => (typeof opt === 'object' ? `${opt.name} ${opt.offset}` : opt)}
    isOptionEqualToValue={(opt, val) => opt?.name === val?.name}
    sx={{ width }}
  />
);

const ScheduleStartTemplate = (props) => {
  const { component_id, isExpanded } = props;

  const [checkKeyError, setCheckKeyError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorDateTime, setErrorDateTime] = useState('');
  const [errorStopDateTime, setErrorStopDateTime] = useState('');
  const [errors, setErrors] = useState([]);
  const [errorDialog, setErrorDialog] = useState(false);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getScheduleStartStore(component_id), [component_id]);
  const data = store((s) => s.data);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);

      // Fetch user info for timezone defaults
      const json = await send_request('authz-service/user-info', '', {});
      if (json?.data) {
        const { timezone } = json.data;
        store.getState().updateData({
          timezone,
          stopCondition: { ...store.getState().data.stopCondition, timezone },
        });
        if (!cancelled) props.setChildTemplateLoaded(true);
      }

      if (component_id === undefined) return;

      if (store.getState().loaded) {
        props.onComponentNameChanged(store.getState().name);
        props.onLastModifiedChanged(store.getState().lastModified);
      } else {
        const json2 = await send_request(
          `project-service/project/component/${component_id}`, '', {}
        ).catch((err) => { throw Error(err.message); });

        if (json2?.data) {
          store.getState().setComponentData(json2.data);
          store.setState({ loaded: true });
          props.onComponentNameChanged(store.getState().name);
          props.onComponentDescriptionChanged(json2.data.description);
          props.onLastModifiedChanged(store.getState().lastModified);
        }
      }

      if (!cancelled) setLoading(false);
      props.setChildTemplateLoaded(true);
    };

    loadData();
    return () => { cancelled = true; };
  }, []);

  const handleKeyChange = (e) => {
    const val = replacePlaceholderValue(e.target.value);
    store.getState().setContent(val, 'key');
    const [hasError, msg] = checkKey(val, store.getState().data);
    setCheckKeyError(hasError);
    setErrorMessage(msg);
  };

  const handleDateChange = (dayjsVal, target) => {
    const dateStr = extractDate(dayjsVal);
    if (!dateStr) return;
    const setter = target === 'stop' ? 'setStopContent' : 'setContent';
    store.getState()[setter](dateStr, 'date');
  };

  const handleTimeChange = (dayjsVal, target) => {
    const timeStr = extractTime(dayjsVal);
    if (!timeStr) return;
    const setter = target === 'stop' ? 'setStopContent' : 'setContent';
    store.getState()[setter](timeStr, 'time');
  };

  const handleChangeTimezone = (tz) => {
    store.getState().updateData({
      timezone: tz,
      stopCondition: { ...store.getState().data.stopCondition, timezone: tz },
    });
  };

  if (loading) return <IndividualComponentLoader />;

  const datePickerSlotProps = { textField: { style: { margin: 0, width: '100%' } } };
  const timePickerSlotProps = { textField: { style: { width: '100%' } } };
  const timeToDayjs = (t) => t ? dayjs(`2024-01-01T${t}`) : null;

  return (
    <CustomPaper style={{ width: '100%' }}>
      {/* Placeholder */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item style={{ flexBasis: 'unset' }} className="schedule-start-label">
          Placeholder
        </Grid>
        <Grid item>
          <W86PlaceholderTextField
            value={data.outputKey}
            error={!!checkKeyError}
            helperText={checkKeyError ? errorMessage : null}
            placeholderText="Enter placeholder for schedule start"
            handleChange={handleKeyChange}
          />
        </Grid>
      </Grid>

      {/* Schedule mode */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item className="schedule-start-label" xs={12}>Start this workflow</Grid>
        <Grid item xs={12}>
          <RadioGroup
            name="startWorkflow"
            value={data.mode}
            onChange={(e) => store.getState().setContent(e.target.value, 'workflow')}
          >
            <FormControlLabel value="specific_date_time" control={<Radio />} label="at a specific date/time" />
            <FormControlLabel value="particular_date_time" control={<Radio />} label="on a particular day/time of the week" />
            <FormControlLabel value="certain_interval" control={<Radio />} label="at a certain interval of time" />
          </RadioGroup>
        </Grid>
      </Grid>

      <ValidationErrors open={errorDialog} close={() => { setErrorDialog(false); setErrors([]); }} errors={errors} />

      {/* Error banner */}
      {(data.mode === 'specific_date_time' || data.mode === 'certain_interval') && errorDateTime && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} className="item-label text-normal">
            <div style={{ background: '#F7E5E9', padding: 8 }}>
              <p className="err-text errorTextFile" style={{ margin: 0 }}>
                <ErrorIcon className="iconDiscard" />
                <span>{errorDateTime}</span>
              </p>
            </div>
          </Grid>
        </Grid>
      )}

      {/* Date picker (specific_date_time or certain_interval) */}
      {(data.mode === 'specific_date_time' || data.mode === 'certain_interval') && (
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item className="schedule-start-label">
            {data.mode === 'certain_interval' ? 'Starting on' : 'At this date'}
          </Grid>
          <Grid item>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                value={data.date ? dayjs(data.date) : null}
                onChange={(v) => handleDateChange(v, 'start')}
                slotProps={datePickerSlotProps}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      )}

      {/* Day selector (particular_date_time) */}
      {data.mode === 'particular_date_time' && (
        <Grid container item xs={12} direction="column" sx={{ mb: 2.5 }}>
          <Grid container item xs={12} direction="row" alignItems="baseline">
            <p className="spaceItems">Select day</p>
            <MUIButtonGroup>
              {(data.selectDays || []).map((day) => (
                <Button
                  key={day.key}
                  className="d-button"
                  variant={day.active ? 'contained' : 'outlined'}
                  color={day.active ? 'primary' : 'info'}
                  onClick={() => store.getState().setContent(day.key, 'selectDays')}
                >
                  {day.name.charAt(0) + day.name.slice(1).toLowerCase()}
                </Button>
              ))}
            </MUIButtonGroup>
          </Grid>
        </Grid>
      )}

      {/* Time picker */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item className="schedule-start-label">At this time</Grid>
        <Grid item>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              label="Time picker"
              value={timeToDayjs(data.time)}
              onChange={(v) => handleTimeChange(v, 'start')}
              slotProps={timePickerSlotProps}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      {/* Timezone */}
      <Grid container spacing={2} alignItems="center">
        <Grid item className="schedule-start-label">Timezone</Grid>
        <Grid item sm={isExpanded ? 4 : 6} xl={isExpanded ? 3 : 6}>
          <TimezoneAutocomplete value={data.timezone} onChange={handleChangeTimezone} />
        </Grid>
      </Grid>

      {/* Interval settings */}
      {data.mode === 'certain_interval' && (
        <Grid spacing={2} sx={{ mt: 2 }}>
          {/* Repeat interval */}
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item className="schedule-start-label">Repeat this workflow every</Grid>
            <Grid item>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <TextField
                    variant="outlined"
                    placeholder="Insert number of times"
                    fullWidth
                    className="key_padding"
                    type="number"
                    value={data.repeat.interval}
                    onChange={(e) => {
                      const num = parseInt(e.target.value, 10);
                      store.getState().setRepeat(isNaN(num) ? '' : num, 'interval');
                    }}
                  />
                </Grid>
                <Grid item>
                  <Select
                    disableUnderline variant="standard" displayEmpty
                    className="schedule-start-select"
                    value={data.repeat.type}
                    onChange={(e) => store.getState().setRepeat(e.target.value, 'type')}
                  >
                    <MenuItem value="day">day(s)</MenuItem>
                    <MenuItem value="businessDays">business day(s)</MenuItem>
                    <MenuItem value="hour">hour(s)</MenuItem>
                    <MenuItem value="minute">minute(s)</MenuItem>
                    <MenuItem value="month">month(s)</MenuItem>
                    <MenuItem value="week">week(s)</MenuItem>
                  </Select>
                </Grid>
                <Grid item className="schedule-start-label-smaller">after the starting date and time</Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Stop condition */}
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item className="schedule-start-label">Stop running the workflow</Grid>
            <Grid item>
              <Select
                disableUnderline variant="standard" displayEmpty
                className="schedule-start-select"
                value={data.stopCondition.type}
                onChange={(e) => store.getState().setStopRunning(e.target.value, 'type')}
              >
                <MenuItem value="after_a_certain_number_of_repeats">after a certain number of repeats</MenuItem>
                <MenuItem value="once_a_date_time_is_reached">once a date/time is reached</MenuItem>
              </Select>
            </Grid>
          </Grid>

          {data.stopCondition.type === 'after_a_certain_number_of_repeats' && (
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
              <Grid item className="item-label text-normal">
                <TextField
                  variant="outlined" placeholder="Insert number of times"
                  fullWidth className="key_padding" type="number"
                  value={data.stopCondition.times}
                  onChange={(e) => {
                    const num = parseInt(e.target.value, 10);
                    store.getState().setStopRunning(isNaN(num) ? '' : num, 'times');
                  }}
                />
              </Grid>
              <Grid item alignItems="center" style={{ display: 'flex' }} className="schedule-start-label-smaller">
                number of times
              </Grid>
            </Grid>
          )}

          {data.stopCondition.type === 'once_a_date_time_is_reached' && errorStopDateTime && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} className="item-label text-normal">
                <div style={{ background: '#F7E5E9', padding: 8 }}>
                  <p className="err-text errorTextFile" style={{ margin: 0 }}>
                    <ErrorIcon className="iconDiscard" />
                    <span>{errorStopDateTime}</span>
                  </p>
                </div>
              </Grid>
            </Grid>
          )}

          {data.stopCondition.type === 'once_a_date_time_is_reached' && (
            <Grid spacing={2}>
              <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                <Grid item className="schedule-start-label">At this date</Grid>
                <Grid item>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="DD.MM.YYYY"
                      value={data.stopCondition.date ? dayjs(data.stopCondition.date) : null}
                      onChange={(v) => handleDateChange(v, 'stop')}
                      slotProps={datePickerSlotProps}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                <Grid item className="schedule-start-label">At this time</Grid>
                <Grid item>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      label="Time picker"
                      value={timeToDayjs(data.stopCondition.time)}
                      onChange={(v) => handleTimeChange(v, 'stop')}
                      slotProps={timePickerSlotProps}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center">
                <Grid item className="schedule-start-label">Timezone</Grid>
                <Grid item xs={isExpanded ? 2 : 6}>
                  <TimezoneAutocomplete
                    value={data.stopCondition.timezone}
                    onChange={(tz) => {
                      store.setState((s) => ({
                        data: { ...s.data, stopCondition: { ...s.data.stopCondition, timezone: tz } },
                      }));
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </CustomPaper>
  );
};

export default ScheduleStartTemplate;
